import type { CrowdExit, Story } from '../data';
import { cardClock } from '../live';
import { num, storyNumber } from '../format';
import {
  matchNames,
  matchNoun,
  matchRule,
  ruleSentence,
  type Rule,
  type RuleGroup,
} from '../rules';
import { positionOf, stakeAt, stakeResult, STEPS } from '../stake';
import { onTick } from './clock';
import { $, $$, attr, segmented, tablist, text } from './dom';
import { inView, reducedMotion, whileVisible } from './motion';

/* -------------------------------------------------------------------------
 * Theme toggle. Same key and values as the dashboard (web/src/shell/theme.ts),
 * so a choice made on one surface holds on the other.
 * ---------------------------------------------------------------------- */
export function themeToggle(button: HTMLElement): void {
  button.addEventListener('click', () => {
    const root = document.documentElement;
    const current =
      root.getAttribute('data-theme') ??
      (window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light');
    const next = current === 'dark' ? 'light' : 'dark';
    root.setAttribute('data-theme', next);
    try {
      localStorage.setItem('rr.theme', next);
    } catch {
      /* the theme still applies for this visit */
    }
  });
}

/* -------------------------------------------------------------------------
 * CountUp: 0 → value with an ease-out quart. Resolves at the final value.
 * ---------------------------------------------------------------------- */
export function countUp(
  el: Element,
  to: number,
  ms: number,
  format: (v: number) => string,
): void {
  if (reducedMotion()) {
    el.textContent = format(to);
    return;
  }
  const start = performance.now();
  const frame = (t: number) => {
    const p = Math.min(1, (t - start) / ms);
    const eased = 1 - (1 - p) ** 4;
    el.textContent = format(Math.round(to * eased));
    if (p < 1) requestAnimationFrame(frame);
  };
  el.textContent = format(0);
  requestAnimationFrame(frame);
}

/** The proof strip: each figure counts up when the strip scrolls into view. */
export function proofStrip(root: HTMLElement): void {
  const figures = $$(root, '[data-countup]');
  inView(root, () => {
    for (const el of figures) {
      const prefix = el.dataset.prefix ?? '';
      countUp(
        el,
        Number(el.dataset.to),
        Number(el.dataset.dur),
        (v) => prefix + num(v),
      );
    }
  });
}

/* -------------------------------------------------------------------------
 * Hero stories: rotate every 9 s with a short fade, pausing under the pointer
 * and while the tab is hidden. No rotation under reduced motion.
 * ---------------------------------------------------------------------- */
export function stories(root: HTMLElement, list: Story[]): void {
  const kicker = $(root, '[data-story-kicker]');
  const figure = $(root, '[data-story-num]');
  const line = $(root, '[data-story-line]');
  if (!kicker || !figure || !line) return;

  let index = 0;
  const show = (story: Story) => {
    text(kicker, story.kicker);
    text(line, story.headline);
    countUp(figure, story.value, 1600, storyNumber);
  };
  countUp(figure, list[0].value, 1600, storyNumber);
  if (reducedMotion() || list.length < 2) return;

  let hovered = false;
  let elapsed = 0;
  root.addEventListener('pointerenter', () => (hovered = true));
  root.addEventListener('pointerleave', () => (hovered = false));
  const STEP = 250;
  whileVisible(() => {
    if (hovered) return;
    elapsed += STEP;
    if (elapsed < 9000) return;
    elapsed = 0;
    index = (index + 1) % list.length;
    root.classList.add('is-out');
    window.setTimeout(() => {
      show(list[index]);
      root.classList.remove('is-out');
    }, 300);
  }, STEP);
}

/* -------------------------------------------------------------------------
 * Email capture: the home page and the expired-link page. subscribe() posts
 * to subscriber.controller.ts, which sends the confirmation email, with the
 * browser's time zone, so the Weekly Sonar comes at 08:00 on this clock. The
 * done message names the address and the first issue, on that clock too.
 * ---------------------------------------------------------------------- */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const INVALID = 'A valid email address is required.';

/** The browser's IANA time zone, or null where Intl will not say. */
export function timeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/** "Monday 5 Oct at 08:00 New York time", or null if the answer has none. */
export async function subscribe(email: string): Promise<string | null> {
  const res = await fetch('/api/subscribers', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, timeZone: timeZone() }),
  });
  if (res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      firstIssue?: unknown;
    };
    return typeof body.firstIssue === 'string' ? body.firstIssue : null;
  }
  let message = 'Something went wrong. Try again in a moment.';
  try {
    const body = (await res.json()) as { message?: unknown };
    if (typeof body.message === 'string') message = body.message;
  } catch {
    /* keep the generic message */
  }
  throw new Error(message);
}

export function signup(root: HTMLElement): void {
  const form = $<HTMLFormElement>(root, 'form');
  const input = $<HTMLInputElement>(root, 'input[type="email"]');
  const button = $<HTMLButtonElement>(root, '[data-signup-submit]');
  const error = $(root, '[data-signup-error]');
  const done = $(root, '[data-signup-done]');
  if (!form || !input || !button || !error || !done) return;

  const fail = (message: string) => {
    text(error, message);
    attr(input, 'aria-invalid', 'true');
    input.focus();
  };
  input.addEventListener('input', () => {
    text(error, '');
    input.removeAttribute('aria-invalid');
  });
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const email = input.value.trim();
    if (!EMAIL.test(email)) return fail(INVALID);
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    subscribe(email)
      .then((first) => {
        text($(done, '[data-signup-email]'), email);
        if (first) text($(done, '[data-signup-first]'), first);
        form.hidden = true;
        done.hidden = false;
        done.focus();
      })
      .catch((cause: unknown) => {
        fail(cause instanceof Error ? cause.message : String(cause));
      })
      .finally(() => {
        button.removeAttribute('aria-busy');
        button.disabled = false;
      });
  });
}

/* -------------------------------------------------------------------------
 * DealCard, live: countdown every tick, the rest when it changes. The cards
 * are real raises (site/live.ts), so they run on the real clock, not the
 * samples' (clock.ts); each carries what its clock needs. Returns what
 * stops the ticking.
 * ---------------------------------------------------------------------- */
const TONES = [
  'tone-critical',
  'tone-urgent',
  'tone-cap',
  'tone-live',
  'tone-neutral',
  'tone-calm',
  'tone-upcoming',
];

function tone(el: Element | null, name: string) {
  if (!el || el.classList.contains(name)) return;
  el.classList.remove(...TONES);
  el.classList.add(name);
}

export function liveFeed(root: HTMLElement): () => void {
  const cards = $$(root, '[data-live-raise]').flatMap((el) => {
    const closesAt = Number(el.dataset.closesAt);
    if (!Number.isFinite(closesAt)) return [];
    const input = { closesAt, nearCap: el.dataset.nearCap !== undefined };
    return [{ el, input }];
  });

  return onTick(() => {
    const n = Date.now();
    for (const { el, input } of cards) {
      const d = cardClock(input, n);
      const timer = $(el, '[data-cd]');
      text(timer, d.countdown);
      attr(timer, 'aria-label', d.spoken);
      tone($(el, '[data-status]'), `tone-${d.statusTone}`);
      text($(el, '[data-status-glyph]'), d.statusGlyph);
      text($(el, '[data-status-label]'), d.statusLabel);
      tone($(el, '[data-closes]'), `tone-${d.urgency}`);
      text($(el, '[data-closes-glyph]'), d.urgencyGlyph);
    }
  });
}

/* -------------------------------------------------------------------------
 * ExitRow list: region filter, log-scale stake slider, one row open.
 * ---------------------------------------------------------------------- */
export function exitsWidget(root: HTMLElement, exits: CrowdExit[]): void {
  const rows = $$(root, '[data-exit]');
  const range = $<HTMLInputElement>(root, '[data-stake-range]');
  const out = $(root, '[data-stake-out]');
  const presets = $(root, '[data-stake-presets]');
  const region = $(root, '[data-exit-region]');
  if (!range || !out || !presets || !region) return;

  segmented(region, (value) => {
    for (const row of rows) {
      row.hidden = value !== 'All' && row.dataset.region !== value;
    }
  });

  const setPreset = segmented(presets, (value) => {
    range.value = String(positionOf(Number(value)));
    update();
  });

  const update = () => {
    const stake = stakeAt(Number(range.value));
    text(out, num(stake));
    attr(range, 'aria-valuetext', num(stake));
    range.style.setProperty('--p', `${(Number(range.value) / STEPS) * 100}%`);
    setPreset(
      ['10', '100', '1000'].includes(String(stake)) ? String(stake) : null,
    );
    for (const row of rows) {
      const exit = exits.find((e) => e.id === row.dataset.exit);
      const r = exit && stakeResult(exit, stake);
      if (!r) continue;
      text($(row, '[data-from]'), r.from);
      text($(row, '[data-to]'), r.to);
    }
  };
  range.addEventListener('input', update);
  update();

  for (const row of rows) {
    const button = $(row, '.exit-row');
    button?.addEventListener('click', () => {
      const opening = !row.classList.contains('is-open');
      for (const other of rows) {
        const open = other === row && opening;
        other.classList.toggle('is-open', open);
        attr($(other, '.exit-row'), 'aria-expanded', String(open));
        const detail = $(other, '.exit-detail');
        if (open) detail?.removeAttribute('aria-hidden');
        else attr(detail, 'aria-hidden', 'true');
      }
    });
  }
}

/* -------------------------------------------------------------------------
 * The part the platforms don't put on the homepage.
 * ---------------------------------------------------------------------- */
/**
 * `meanings` is the card beside the waffle that says what each outcome means
 * for your shares: it lights the same outcome, and pointing at one of its rows
 * selects that outcome as the legend does.
 */
export function waffle(root: HTMLElement, meanings: HTMLElement | null): void {
  const legend = $$<HTMLButtonElement>(root, '.waffle-legend button');
  const dots = $$(root, '.waffle .dot');
  const rows = meanings ? $$(meanings, '[data-outcome]') : [];
  const figure = $(root, '[data-waffle-num]');
  const label = $(root, '[data-waffle-label]');
  let current = 'exited';

  const select = (id: string) => {
    if (id === current) return;
    const b = legend.find((x) => x.dataset.outcome === id);
    if (!b) return;
    current = id;
    legend.forEach((x) => attr(x, 'aria-pressed', String(x === b)));
    for (const el of [...dots, ...rows]) {
      el.classList.toggle('is-dim', el.dataset.outcome !== id);
    }
    text(figure, `${b.dataset.count}%`);
    text(label, b.dataset.label ?? '');
    figure?.classList.toggle('tone-text-gain', b.dataset.tone === 'gain');
    figure?.classList.toggle('tone-text-loss', b.dataset.tone === 'loss');
    figure?.classList.toggle('tone-text-none', b.dataset.tone === 'none');
  };
  for (const b of legend) {
    const id = b.dataset.outcome ?? '';
    b.addEventListener('click', () => select(id));
    b.addEventListener('pointerenter', () => select(id));
    b.addEventListener('focus', () => select(id));
  }
  for (const row of rows) {
    row.addEventListener('pointerenter', () =>
      select(row.dataset.outcome ?? ''),
    );
  }
  $(root, '.waffle')?.addEventListener('pointerover', (e) => {
    const id = (e.target as HTMLElement).dataset?.outcome;
    if (id) select(id);
  });
}

export function freetrade(
  root: HTMLElement,
  options: { id: string; stat: string; statLabel: string; tone: string }[],
): void {
  const buttons = $$<HTMLButtonElement>(root, 'button[data-ft]');
  const figure = $(root, '[data-ft-num]');
  const label = $(root, '[data-ft-label]');
  const select = (id: string) => {
    const o = options.find((x) => x.id === id);
    if (!o) return;
    for (const b of buttons) {
      const on = b.dataset.ft === id;
      attr(b, 'aria-pressed', String(on));
      if (b.classList.contains('ft-row')) b.classList.toggle('is-dim', !on);
    }
    text(figure, o.stat);
    text(label, o.statLabel);
    figure?.classList.toggle('tone-text-gain', o.tone === 'gain');
    figure?.classList.toggle('tone-text-loss', o.tone === 'loss');
  };
  for (const b of buttons)
    b.addEventListener('click', () => select(b.dataset.ft ?? ''));
}

/** The Reg CF bar's flashlight follows the pointer across the dark half. */
export function flashlight(seg: HTMLElement): void {
  seg.addEventListener('pointermove', (e) => {
    const r = seg.getBoundingClientRect();
    seg.style.setProperty('--fx', `${e.clientX - r.left}px`);
    seg.style.setProperty('--fy', `${e.clientY - r.top}px`);
  });
}

/* -------------------------------------------------------------------------
 * HowItWorksStepper: three tabs that advance every 8 s once the section is
 * in view, pause under the pointer, and stop for good after a click.
 * ---------------------------------------------------------------------- */
export function stepper(
  root: HTMLElement,
  onPanel: (index: number) => void,
): void {
  const how = $(root, '.how');
  const list = $(root, '[role="tablist"]');
  const tabs = $$(root, '[role="tab"]');
  const panels = $$(root, '[role="tabpanel"]');
  if (!how || !list) return;

  let active = 0;
  let auto = !reducedMotion();
  let started = false;

  const time = () => {
    tabs.forEach((t) => t.classList.remove('is-timing'));
    if (!auto || !started) return;
    const tab = tabs[active];
    void tab.offsetWidth; // restart the progress bar's animation
    tab.classList.add('is-timing');
  };

  const show = (index: number) => {
    active = index;
    tabs.forEach((t, i) => t.classList.toggle('is-on', i === index));
    panels.forEach((p, i) => p.classList.toggle('is-on', i === index));
    onPanel(index);
    time();
  };

  const select = tablist(list, (index, byUser) => {
    if (byUser) auto = false;
    show(index);
  });

  for (const tab of tabs) {
    $(tab, '.how-prog')?.addEventListener('animationend', () => {
      if (!auto) return;
      const next = (active + 1) % tabs.length;
      select(next);
      show(next);
    });
  }
  how.addEventListener('pointerenter', () => how.classList.add('is-paused'));
  how.addEventListener('pointerleave', () => how.classList.remove('is-paused'));
  document.addEventListener('visibilitychange', () =>
    how.classList.toggle('is-paused', document.hidden),
  );
  inView(root, () => {
    started = true;
    time();
  });
  onPanel(0);
}

/**
 * Step 02: the rule builder, matching against today's live raises: the
 * groups the page drew them in (site/rules.ts). Without them, only the
 * sentence follows the chips.
 */
export function ruleBuilder(
  root: HTMLElement,
  groups: RuleGroup[] | null,
  initial: Rule,
): void {
  const rule: Rule = {
    regions: [...initial.regions],
    sectors: [...initial.sectors],
    ticketCap: initial.ticketCap,
  };
  const toggle = <T>(list: T[], v: T) =>
    list.includes(v) ? list.filter((x) => x !== v) : [...list, v];

  const render = () => {
    text($(root, '[data-rule-sentence]'), ruleSentence(rule));
    if (!groups) return;
    const match = matchRule(rule, groups);
    text($(root, '[data-rule-n]'), num(match.count));
    text($(root, '[data-rule-noun]'), matchNoun(match.count));
    text($(root, '[data-rule-names]'), matchNames(match));
    const hit = $(root, '.rule-hit');
    const names = $(root, '[data-rule-names]');
    const empty = $(root, '[data-rule-empty]');
    if (hit) hit.hidden = match.count === 0;
    if (names) names.hidden = match.count === 0;
    if (empty) empty.hidden = match.count > 0;
  };

  for (const b of $$<HTMLButtonElement>(root, '.chip')) {
    b.addEventListener('click', () => {
      const pressed = b.getAttribute('aria-pressed') !== 'true';
      attr(b, 'aria-pressed', String(pressed));
      if (b.dataset.ruleRegion) {
        rule.regions = toggle(
          rule.regions,
          b.dataset.ruleRegion as Rule['regions'][number],
        );
      } else if (b.dataset.ruleSector) {
        rule.sectors = toggle(rule.sectors, b.dataset.ruleSector);
      } else {
        rule.ticketCap = pressed;
      }
      render();
    });
  }
}

/**
 * Step 03: the alert timeline. The dot is a CSS animation; the notification
 * reads the same 8 s loop four times a second to name the last marker passed.
 */
export function timeline(
  root: HTMLElement,
  markers: readonly { at: number; kicker: string | null }[],
): { start: () => void; stop: () => void } {
  const kicker = $(root, '[data-tl-kicker]');
  const LOOP = 8000;
  let t0 = 0;
  let timer: number | undefined;

  const paint = (passed: number) => {
    const sent = markers.filter((m, i) => i <= passed && m.kicker);
    text(kicker, sent[sent.length - 1]?.kicker ?? markers[0].kicker ?? '');
  };

  const read = () => {
    const phase = ((performance.now() - t0) % LOOP) / LOOP;
    let passed = 0;
    markers.forEach((m, i) => {
      if (m.at <= phase) passed = i;
    });
    paint(passed);
  };

  return {
    start() {
      if (reducedMotion()) return;
      root.classList.remove('is-running');
      void root.offsetWidth;
      root.classList.add('is-running');
      t0 = performance.now();
      window.clearInterval(timer);
      timer = whileVisible(read, 250);
    },
    stop() {
      root.classList.remove('is-running');
      window.clearInterval(timer);
      paint(0);
    },
  };
}

/** The teaser's Free card: back to the top, into the email field. */
export function freeCard(link: HTMLAnchorElement): void {
  link.addEventListener('click', (e) => {
    const input = $<HTMLInputElement>(document, '#signup-email');
    if (!input || input.closest('[hidden]')) return;
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: reducedMotion() ? 'auto' : 'smooth' });
    input.focus({ preventScroll: true });
  });
}

/* -------------------------------------------------------------------------
 * Accordion: at most one open.
 * ---------------------------------------------------------------------- */
export function accordion(root: HTMLElement): void {
  const items = $$(root, '.acc-item');
  for (const item of items) {
    // The server leaves every answer readable, for a page with no script.
    if (!item.classList.contains('is-open')) {
      attr($(item, '.acc-p'), 'aria-hidden', 'true');
    }
    $(item, '.acc-btn')?.addEventListener('click', () => {
      const opening = !item.classList.contains('is-open');
      for (const other of items) {
        const open = other === item && opening;
        other.classList.toggle('is-open', open);
        attr($(other, '.acc-btn'), 'aria-expanded', String(open));
        const panel = $(other, '.acc-p');
        if (open) panel?.removeAttribute('aria-hidden');
        else attr(panel, 'aria-hidden', 'true');
      }
    });
  }
}
